import React, { useEffect, useState } from "react";
import { Globe, RefreshCw, Save, ExternalLink, Clock, CheckCircle2, AlertTriangle, Sparkles } from "lucide-react";
import api from "../lib/api";
import { useWorkspace } from "../contexts/WorkspaceContext";

const fmtTime = (iso) => {
  if (!iso) return "Never";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export default function HotelWebsite() {
  const { currentWorkspaceId } = useWorkspace();
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    if (!currentWorkspaceId) return;
    try {
      const { data } = await api.get(`/workspaces/${currentWorkspaceId}/hotel-website`);
      setConfig(data.config);
      setHistory(data.history || []);
      setUrl(data.config?.url || "");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentWorkspaceId]);

  const save = async () => {
    if (!currentWorkspaceId) return;
    setSaving(true); setError(null);
    try {
      await api.put(`/workspaces/${currentWorkspaceId}/hotel-website`, { url });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const syncNow = async () => {
    if (!currentWorkspaceId) return;
    setSyncing(true); setError(null);
    try {
      const { data } = await api.post(`/workspaces/${currentWorkspaceId}/hotel-website/sync`, {});
      setConfig(data.config);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const lastStatus = config?.last_sync_status;
  const statusBadge =
    lastStatus === "synced"  ? { cls: "bg-[#10B981]/15 border-[#10B981]/40 text-[#86EFAC]", icon: CheckCircle2, text: "Up to date" } :
    lastStatus === "changed" ? { cls: "bg-[#D4A968]/15 border-[#D4A968]/40 text-[#D4A968]", icon: Sparkles,      text: "Change detected" } :
    lastStatus === "error"   ? { cls: "bg-[#EF4444]/15 border-[#EF4444]/40 text-[#FCA5A5]", icon: AlertTriangle, text: "Last sync failed" } :
                               { cls: "bg-zinc-800 border-zinc-700 text-zinc-400", icon: Clock, text: "Not yet synced" };

  return (
    <div className="px-6 py-6 max-w-5xl mx-auto" data-testid="hotel-website-page">
      <div className="mb-6">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-0.5">
          External Feeds · Daily Sync
        </div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Globe className="w-5 h-5 text-[#D4A968]" /> Hotel Website
        </h1>
        <p className="text-sm text-zinc-400 mt-1">
          Paste your hotel's public website URL. Vorion fetches it every <span className="text-[#D4A968]">24 hours</span> and surfaces a snapshot + change log so you never miss a content update.
        </p>
      </div>

      {/* URL form */}
      <section className="bg-[#141417] border border-zinc-800 rounded-lg p-4 mb-6">
        <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Website URL</label>
        <div className="flex gap-2">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.your-hotel.com"
            data-testid="hotel-website-url-input"
            className="flex-1 bg-[#0B0B0E] border border-zinc-800 focus:border-[#D4A968] rounded px-3 py-2 text-sm text-white outline-none"
          />
          <button
            type="button" onClick={save} disabled={saving}
            data-testid="hotel-website-save"
            className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded bg-[#D4A968] hover:bg-[#E8C285] text-black font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save"}
          </button>
          <button
            type="button" onClick={syncNow} disabled={syncing || !config?.url}
            data-testid="hotel-website-sync"
            className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded bg-[#10B981]/15 border border-[#10B981]/40 text-[#86EFAC] hover:bg-[#10B981]/25 disabled:opacity-40 inline-flex items-center gap-1.5"
          >
            <RefreshCw className={`w-3 h-3 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync now"}
          </button>
        </div>
        {error && <div className="mt-3 text-xs text-[#FCA5A5] bg-[#EF4444]/10 border border-[#EF4444]/30 rounded p-2" data-testid="hotel-website-error">{error}</div>}
      </section>

      {/* Status + latest snapshot */}
      {config?.url && (
        <section className="bg-[#141417] border border-zinc-800 rounded-lg p-4 mb-6" data-testid="hotel-website-status">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="min-w-0">
              <a
                href={config.url} target="_blank" rel="noreferrer"
                className="text-base text-[#D4A968] hover:underline inline-flex items-center gap-1 truncate"
              >
                {config.url} <ExternalLink className="w-3 h-3" />
              </a>
              <div className="text-xs text-zinc-500 mt-0.5 font-mono">
                Last sync: {fmtTime(config.last_synced_at)}
              </div>
            </div>
            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border text-[10px] font-mono uppercase tracking-widest ${statusBadge.cls}`}>
              <statusBadge.icon className="w-3 h-3" /> {statusBadge.text}
            </span>
          </div>
          {config.last_error && (
            <div className="text-xs text-[#FCA5A5] font-mono">{config.last_error}</div>
          )}
          {config.latest_snapshot && (
            <div className="border-t border-zinc-800 mt-3 pt-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Latest snapshot</div>
              <h3 className="text-sm font-semibold text-white">{config.latest_snapshot.title}</h3>
              <p className="text-sm text-zinc-300 mt-1 leading-relaxed">{config.latest_snapshot.summary}</p>
              <div className="mt-2 text-[10px] text-zinc-500 font-mono">
                {config.latest_snapshot.byte_size?.toLocaleString()} chars · fetched {fmtTime(config.latest_snapshot.fetched_at)}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Change log */}
      {history.length > 0 && (
        <section className="bg-[#141417] border border-zinc-800 rounded-lg overflow-hidden" data-testid="hotel-website-history">
          <div className="bg-[#0F0F12] px-4 py-2.5 border-b border-zinc-800 flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#D4A968]" />
            <h2 className="text-sm font-semibold text-white">Change log</h2>
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 ml-auto">
              {history.length} {history.length === 1 ? "entry" : "entries"}
            </span>
          </div>
          <ul className="divide-y divide-zinc-800/70">
            {history.map((h, idx) => (
              <li key={`${h.content_hash}-${idx}`} className="px-4 py-3" data-testid={`hotel-website-history-row-${idx}`}>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-[#D4A968]">{h.change_summary}</span>
                  <span className="text-[10px] font-mono text-zinc-500 ml-auto">{fmtTime(h.fetched_at)}</span>
                </div>
                <div className="text-sm text-zinc-200 mt-0.5 truncate">{h.title}</div>
                <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{h.summary}</p>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
